import React, { useCallback, useMemo, useState } from 'react';
import { path, prop } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector } from 'js/store/hooks';
import { Category } from '../Category';
import { trackGameCategorySwitch } from '../trackingApi';
import { usePortletConfig, usePortletSelector } from 'js/components/system/Portlet';
import { usePrevious } from 'js/hooks/usePrevious';
import { useUpdateEffect } from 'js/hooks/useUpdateEffect';
import style from './index.scss';

export const useTrackCategoryChange = (activeCategory) => {
    const feedName = usePortletSelector(prop('feedName'));
    const { reactPortletId } = usePortletConfig();
    const prevCid = usePrevious(activeCategory.cid);

    useUpdateEffect(() => {
        if (prevCid !== activeCategory.cid) {
            trackGameCategorySwitch(reactPortletId, feedName, activeCategory.name);
        }
    }, [reactPortletId, feedName, activeCategory, prevCid]);
};

export const CategoryTab = ({ category, onTabClick, isActive }) => {
    const t = useTranslations();
    const { cid, roundIcon, displayName, displayNameShort, mostPlayedGameCode } = category;
    const iconClass = style[roundIcon] || style.generic;
    const activeClass = isActive ? style.active : '';
    const fallbackDisplayName = t(displayName);
    const onClick = useCallback(() => {
        onTabClick(cid);
    }, [cid, onTabClick]);
    const mostPlayedGame = useAppSelector(path(['games', mostPlayedGameCode, 'name']));

    return (
        <div className={style.category} onClick={onClick}>
            <div className={`${style.icon} ${iconClass} ${activeClass}`} />
            <div className={`${style.title} ${activeClass}`}>
                {t(displayNameShort, [mostPlayedGame], fallbackDisplayName)}
            </div>
        </div>
    );
};

export const CategoriesTabs = ({ categories = [] }) => {
    const [activeCategoryId, setActiveCategoryId] = useState(path([0, 'cid'], categories));
    const activeCategory = useMemo(
        () => categories.find(({ cid }) => cid === activeCategoryId) || categories[0],
        [categories, activeCategoryId]
    );

    useTrackCategoryChange(activeCategory);

    if (!activeCategory) {
        return null;
    }

    return <>
        <div className={style.container}>
            {categories.map(category => <CategoryTab
                key={category.cid}
                category={category}
                onTabClick={setActiveCategoryId}
                isActive={category.cid === activeCategory.cid}
            />)}
        </div>
        <Category showTitle={false} {...activeCategory} />
    </>;
};

export default CategoriesTabs;
